import { action, makeAutoObservable, observable } from "mobx";
import Store from "./Store";
import {BillingPlanDto, MyBillingDto, SubscriptionPlanDto, SubscriptionState} from "../apis/PaymentApi";

export default class SubscriptionPlanStore {
  subscriptionPlans: SubscriptionPlanDto[] = [];
  myBillings: MyBillingDto[] | null = null;
  billingPlan: BillingPlanDto | null = null;
  isLoading: boolean = false;
  error: string | null = null;

  constructor(private store: Store) {
    makeAutoObservable(this, {
      subscriptionPlans: observable,
      myBillings: observable,
      billingPlan: observable,
      isLoading: observable,
      error: observable,
      loadSubscriptionPlans: action.bound,
      loadBillingLimits: action.bound,
      cancelSubscription: action.bound,
      setError: action.bound,
    });
  }

  async cancelSubscription(id: string, shortToken: string | null = null): Promise<void> {
    if (!this.store.api) {
      this.error = "API не инициализирован";
      throw new Error(this.error);
    }

    try {
      this.isLoading = true;
      this.error = null;

      await this.store.api.paymentApi.cancelSubscription(id, shortToken);
      await this.loadSubscriptionPlans();
    } catch (err: any) {
      console.error("Ошибка при отмене подписки:", err);
      this.error =
        err?.response?.data?.message ||
        err?.message ||
        "Не удалось отменить подписку. Попробуйте еще раз.";
      throw err;
    } finally {
      this.isLoading = false;
    }
  }

  async loadSubscriptionPlans(shortToken: string | null = null) {
    if (!this.store.api) {
      this.error = "API не инициализирован";
      return;
    }

    try {
      this.isLoading = true;
      this.error = null;
      const [plans, billings] = await Promise.all([
        this.store.api.paymentApi.getSubscriptionPlans(),
        this.store.api.paymentApi
          .getMyBilling(shortToken)
          .catch(() => null as MyBillingDto[] | null),
      ]);

      const billingArray: MyBillingDto[] = Array.isArray(billings)
        ? billings
        : billings
        ? [billings as unknown as MyBillingDto]
        : [];

      this.myBillings = billingArray.length > 0 ? billingArray : null;

      const activePlanIds = new Set(
        billingArray
          .filter((b) => b.isActive && b.subscriptionPlan?.id)
          .map((b) => b.subscriptionPlan!.id)
      );

      this.subscriptionPlans = plans.map((plan) => {
        const billing = billingArray.find((b) => b.subscriptionPlan?.id === plan.id);
        return {
          ...plan,
          isActive: billing?.isActive ?? false,
          state: billing?.state ?? SubscriptionState.NotActive,
        }
      });
    } catch (err: any) {
      console.error("Ошибка при загрузке тарифных планов:", err);
      this.error =
        err.response?.data?.message ||
        err.message ||
        "Не удалось загрузить тарифные планы";
      this.subscriptionPlans = [];
      this.myBillings = null;
    } finally {
      this.isLoading = false;
    }
  }

  async loadBillingLimits(shortToken: string | null = null) {
    if (!this.store.api) {
      this.error = "API не инициализирован";
      return;
    }

    try {
      this.isLoading = true;
      this.error = null;

      this.billingPlan = await this.store.api.paymentApi.getBillingLimits(shortToken);
    } catch (err: any) {
      console.error("Ошибка при загрузке лимитов тарифа:", err);
      this.error =
        err.response?.data?.message ||
        err.message ||
        "Не удалось загрузить лимиты тарифа";
      this.billingPlan = null;
    } finally {
      this.isLoading = false;
    }
  }

  getCurrentBillingPlan(): BillingPlanDto | undefined {
    return this.billingPlan ?? undefined;
  }

  setError(error: string | null) {
    this.error = error;
  }

  getActivePlans(): SubscriptionPlanDto[] {
    return this.subscriptionPlans.filter((plan) => plan.isActive && plan.state === SubscriptionState.Active);
  }
}








